import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, List, ListItem, Stack, Typography } from '@mui/material';
import { Close as CloseIcon, Undo as UndoIcon } from '@mui/icons-material';
import { OGNI_GRUPPO, TESTO_OGNI_GRUPPO } from './concessioni';

// Quanti gruppi una riga di ruolo mostra prima di «mostra tutti»: i gruppi non hanno un
// numero massimo, e una persona che lavora trenta code non deve spingere le altre
// concessioni fuori dalla finestra.
const GRUPPI_VISIBILI = 8;

/**
 * Una concessione dentro la riga del suo ruolo: il gruppo, e lo stato se non e' salvata.
 * La croce revoca (o toglie l'aggiunta preparata); su una concessione «da rimuovere» la
 * croce diventa il ripristino.
 */
function ChipConcessione({ riga, canUpdate, onRevoke, onRipristina }) {
    const gruppo = riga.groupId ? riga.groupLabel || riga.groupId : TESTO_OGNI_GRUPPO;
    const daRimuovere = riga.stato === 'da-rimuovere';
    const daAggiungere = riga.stato === 'da-aggiungere';
    const etichetta = (
        <>
            {gruppo}
            {riga.stato !== 'salvata' && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                    {daRimuovere ? 'da rimuovere' : 'da aggiungere'}
                </Typography>
            )}
        </>
    );
    let azione;
    if (canUpdate) {
        azione = daRimuovere
            ? { onDelete: () => onRipristina(riga), deleteIcon: <UndoIcon aria-label={`ripristina ${riga.roleLabel} su ${gruppo}`} /> }
            : { onDelete: () => onRevoke(riga), deleteIcon: <CloseIcon aria-label={`revoca ${riga.roleLabel} su ${gruppo}`} /> };
    }
    return (
        <Chip
            size="small"
            label={etichetta}
            variant={riga.stato === 'salvata' ? 'filled' : 'outlined'}
            color={daRimuovere ? 'error' : daAggiungere ? 'primary' : 'default'}
            sx={daRimuovere ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}
            {...azione}
        />
    );
}

ChipConcessione.propTypes = {
    riga: PropTypes.object.isRequired,
    canUpdate: PropTypes.bool.isRequired,
    onRevoke: PropTypes.func.isRequired,
    onRipristina: PropTypes.func.isRequired,
};

/**
 * Le concessioni di una persona, una riga per ruolo con i suoi gruppi.
 *
 * Con molti gruppi un elenco piatto ripete lo stesso ruolo decine di volte, e non si legge
 * piu' «che cosa fa questa persona». Quali righe siano salvate, da aggiungere o da rimuovere
 * lo decide il pannello: qui si mostrano soltanto, e revoca e ripristino risalgono a lui.
 */
export default function ConcessioniPerRuolo({
    voci,
    permessiPerRuolo,
    totalPermissions,
    canUpdate,
    vuoto,
    onRevoke,
    onRipristina,
}) {
    // I ruoli di cui la riga mostra tutti i gruppi, oltre ai primi GRUPPI_VISIBILI.
    const [ruoliEspansi, setRuoliEspansi] = useState(() => new Set());

    const toggleEspanso = (roleId) => {
        setRuoliEspansi((prev) => {
            const next = new Set(prev);
            if (next.has(roleId)) {
                next.delete(roleId);
            } else {
                next.add(roleId);
            }
            return next;
        });
    };

    return (
        <List dense>
            {voci.map((voce) => {
                const espanso = ruoliEspansi.has(voce.roleId);
                const visibili = espanso
                    ? voce.righe
                    : voce.righe.slice(0, GRUPPI_VISIBILI);
                const nascosti = voce.righe.length - visibili.length;
                return (
                    <ListItem
                        key={voce.roleId}
                        alignItems="flex-start"
                        sx={{ display: 'block', px: 0 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="body2" fontWeight={500}>
                                {voce.roleLabel}
                            </Typography>
                            {permessiPerRuolo.get(voce.roleId) != null
                                && totalPermissions != null && (
                                <Typography variant="caption"
                                            color="text.secondary">
                                    {permessiPerRuolo.get(voce.roleId)}
                                    {' / '}
                                    {totalPermissions}
                                </Typography>
                            )}
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {visibili.map((riga) => (
                                <ChipConcessione
                                    key={`${riga.stato}:${riga.groupId || OGNI_GRUPPO}`}
                                    riga={riga}
                                    canUpdate={canUpdate}
                                    onRevoke={onRevoke}
                                    onRipristina={onRipristina}
                                />
                            ))}
                            {(nascosti > 0 || espanso)
                                && voce.righe.length > GRUPPI_VISIBILI && (
                                <Button
                                    size="small"
                                    onClick={() => toggleEspanso(voce.roleId)}
                                    sx={{ py: 0, minWidth: 0 }}
                                >
                                    {espanso ? 'mostra meno' : `+${nascosti} altri`}
                                </Button>
                            )}
                        </Box>
                    </ListItem>
                );
            })}
            {vuoto && (
                <Typography variant="body2" color="text.secondary">
                    Nessun ruolo: questa persona non vede i dati di
                    alcun gruppo.
                </Typography>
            )}
        </List>
    );
}

ConcessioniPerRuolo.propTypes = {
    voci: PropTypes.arrayOf(PropTypes.shape({
        roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        roleLabel: PropTypes.string,
        righe: PropTypes.array.isRequired,
    })).isRequired,
    permessiPerRuolo: PropTypes.instanceOf(Map).isRequired,
    totalPermissions: PropTypes.number,
    canUpdate: PropTypes.bool.isRequired,
    vuoto: PropTypes.bool.isRequired,
    onRevoke: PropTypes.func.isRequired,
    onRipristina: PropTypes.func.isRequired,
};
